import {
  IconClear,
  IconDone,
  IconInfoOutlined,
  IconTime,
  IconWarningOutline,
} from '@capitalise/ui-design-system';

export const FundingMatchStatus = Object.freeze({
  submitted: 0,
  inReview: 1,
  passed: 2,
  paidOut: 3,
  lenderDecline: 4,
  notAppropriate: 5,
  incomplete: 6,
  contactMade: 8,
  indicativeOffer: 9,
  inUnderwriting: 11,
  cancelled: 12,
  underwrittenOffer: 14,
  clientAccepted: 15,
  clientDecline: 16,
  nonResponsive: 17,
  lost: 18,
  awaitingClientDecision: 19,
  onHold: 21,
  lenderRejected: 22,
  awaitingClientContact: 23,
  professionsInstructed: 24,
  professionsSignOff: 25,
  finalCreditSignOff: 26,
});

export const FundingMatchStatusLabel = Object.freeze({
  [FundingMatchStatus.submitted]: 'Submitted',
  [FundingMatchStatus.inReview]: 'In Review',
  [FundingMatchStatus.passed]: 'Passed',
  [FundingMatchStatus.paidOut]: 'Paid Out',
  [FundingMatchStatus.lenderDecline]: 'Lender Declined',
  [FundingMatchStatus.notAppropriate]: 'N/A',
  [FundingMatchStatus.incomplete]: 'Incomplete',
  [FundingMatchStatus.contactMade]: 'Contact Made',
  [FundingMatchStatus.indicativeOffer]: 'Indicative Offer',
  [FundingMatchStatus.inUnderwriting]: 'In Underwriting',
  [FundingMatchStatus.cancelled]: 'Cancelled',
  [FundingMatchStatus.underwrittenOffer]: 'Underwritten Offer',
  [FundingMatchStatus.clientAccepted]: 'Client Preferred',
  [FundingMatchStatus.clientDecline]: 'Client Declined',
  [FundingMatchStatus.nonResponsive]: 'Closed, no contact',
  [FundingMatchStatus.lost]: 'Lost',
  [FundingMatchStatus.awaitingClientDecision]: 'Awaiting Client Decision',
  [FundingMatchStatus.onHold]: 'On Hold',
  [FundingMatchStatus.lenderRejected]: 'Failed',
  [FundingMatchStatus.awaitingClientContact]: 'Awaiting Client Contact',
  [FundingMatchStatus.professionsInstructed]: 'Professions Instructed',
  [FundingMatchStatus.professionsSignOff]: 'Professions Sign Off',
  [FundingMatchStatus.finalCreditSignOff]: 'Final Credit Sign Off',
});

export const FundingMatchStatusColor = Object.freeze({
  [FundingMatchStatus.submitted]: 'grey-500',
  [FundingMatchStatus.inReview]: 'green-500',
  [FundingMatchStatus.passed]: 'danger-500',
  [FundingMatchStatus.paidOut]: 'green-500',
  [FundingMatchStatus.lenderDecline]: 'danger-500',
  [FundingMatchStatus.notAppropriate]: 'danger-500',
  [FundingMatchStatus.incomplete]: 'grey-500',
  [FundingMatchStatus.contactMade]: 'grey-500',
  [FundingMatchStatus.indicativeOffer]: 'green-500',
  [FundingMatchStatus.inUnderwriting]: 'grey-500',
  [FundingMatchStatus.cancelled]: 'danger-500',
  [FundingMatchStatus.underwrittenOffer]: 'green-500',
  [FundingMatchStatus.clientAccepted]: 'green-500',
  [FundingMatchStatus.clientDecline]: 'danger-500',
  [FundingMatchStatus.nonResponsive]: 'grey-500',
  [FundingMatchStatus.lost]: 'danger-500',
  [FundingMatchStatus.awaitingClientDecision]: 'warning-500',
  [FundingMatchStatus.onHold]: 'grey-500',
  [FundingMatchStatus.lenderRejected]: 'warning-500',
  [FundingMatchStatus.awaitingClientContact]: 'warning-500',
  [FundingMatchStatus.professionsInstructed]: 'grey-500',
  [FundingMatchStatus.professionsSignOff]: 'green-500',
  [FundingMatchStatus.finalCreditSignOff]: 'green-500',
});

export const FundingMatchStatusIcon = Object.freeze({
  [FundingMatchStatus.inReview]: <IconDone />,
  [FundingMatchStatus.passed]: <IconClear />,
  [FundingMatchStatus.paidOut]: <IconDone />,
  [FundingMatchStatus.lenderDecline]: <IconWarningOutline />,
  [FundingMatchStatus.notAppropriate]: <IconClear />,
  [FundingMatchStatus.indicativeOffer]: <IconDone />,
  [FundingMatchStatus.cancelled]: <IconClear />,
  [FundingMatchStatus.underwrittenOffer]: <IconDone />,
  [FundingMatchStatus.clientAccepted]: <IconDone />,
  [FundingMatchStatus.clientDecline]: <IconClear />,
  [FundingMatchStatus.lost]: <IconClear />,
  [FundingMatchStatus.awaitingClientDecision]: <IconTime />,
  [FundingMatchStatus.lenderRejected]: <IconInfoOutlined />,
  [FundingMatchStatus.awaitingClientContact]: <IconTime />,
  [FundingMatchStatus.professionsSignOff]: <IconDone />,
  [FundingMatchStatus.finalCreditSignOff]: <IconDone />,
});

export const FundingMatchStatusVariant = Object.freeze({
  [FundingMatchStatus.lenderDecline]: 'secondary',
  [FundingMatchStatus.lenderRejected]: 'secondary',
  [FundingMatchStatus.submitted]: 'filled',
  [FundingMatchStatus.inReview]: 'filled',
  [FundingMatchStatus.passed]: 'filled',
  [FundingMatchStatus.paidOut]: 'filled',
  [FundingMatchStatus.notAppropriate]: 'filled',
  [FundingMatchStatus.incomplete]: 'filled',
  [FundingMatchStatus.contactMade]: 'filled',
  [FundingMatchStatus.indicativeOffer]: 'filled',
  [FundingMatchStatus.inUnderwriting]: 'filled',
  [FundingMatchStatus.cancelled]: 'filled',
  [FundingMatchStatus.underwrittenOffer]: 'filled',
  [FundingMatchStatus.clientAccepted]: 'filled',
  [FundingMatchStatus.clientDecline]: 'filled',
  [FundingMatchStatus.nonResponsive]: 'filled',
  [FundingMatchStatus.lost]: 'filled',
  [FundingMatchStatus.awaitingClientDecision]: 'filled',
  [FundingMatchStatus.onHold]: 'filled',
  [FundingMatchStatus.awaitingClientContact]: 'filled',
  [FundingMatchStatus.professionsInstructed]: 'filled',
  [FundingMatchStatus.professionsSignOff]: 'filled',
  [FundingMatchStatus.finalCreditSignOff]: 'filled',
});

export const FundingMatchReportStatusLabel = Object.freeze({
  [FundingMatchStatus.indicativeOffer]: 'Indicative',
  [FundingMatchStatus.underwrittenOffer]: 'Underwritten',
});

export const FundingMatchStatusRejectNote = Object.freeze({
  [FundingMatchStatus.lenderDecline]: 'This lender has declined to proceed',
  [FundingMatchStatus.nonResponsive]: 'The lender was unable to make contact',
  [FundingMatchStatus.clientDecline]: 'You declined to continue with this lender',
  [FundingMatchStatus.lenderRejected]: 'This lender was unable to process your application',
  [FundingMatchStatus.passed]: 'The application did not meet the lenders suitability checks',
  default: 'This lender has not provided a response yet. We’ll update you soon.',
});

export const progressStatuses = (status) => {
  const optionalStatus = [
    FundingMatchStatus.professionsInstructed,
    FundingMatchStatus.professionsSignOff,
    FundingMatchStatus.finalCreditSignOff,
  ];

  return [
    { value: false },
    {
      label: toLabel(FundingMatchStatus.inReview),
      value: FundingMatchStatus.inReview,
    },
    { value: FundingMatchStatus.contactMade },
    {
      label: toLabel(FundingMatchStatus.indicativeOffer),
      value: FundingMatchStatus.indicativeOffer,
    },
    { value: FundingMatchStatus.awaitingClientDecision },
    {
      label: toLabel(FundingMatchStatus.inUnderwriting),
      value: FundingMatchStatus.inUnderwriting,
    },
    { value: FundingMatchStatus.underwrittenOffer },
    ...(!optionalStatus.includes(status)
      ? []
      : [
          {
            value: status,
          },
        ]),
    {
      label: toLabel(FundingMatchStatus.paidOut),
      value: FundingMatchStatus.paidOut,
    },
  ];
};

export function isValid(type) {
  return Object.values(FundingMatchStatus).find((elem) => elem === type);
}

export function toLabel(type) {
  return FundingMatchStatusLabel[type];
}

export function getInProgressStatus(isCommercialProperty) {
  return [
    FundingMatchStatus.inReview,
    FundingMatchStatus.contactMade,
    FundingMatchStatus.indicativeOffer,
    FundingMatchStatus.awaitingClientDecision,
    FundingMatchStatus.inUnderwriting,
    FundingMatchStatus.underwrittenOffer,
    ...(!isCommercialProperty
      ? []
      : [
          FundingMatchStatus.professionsInstructed,
          FundingMatchStatus.professionsSignOff,
          FundingMatchStatus.finalCreditSignOff,
        ]),
    FundingMatchStatus.paidOut,
  ];
}

const underOfferStatuses = Object.freeze([
  FundingMatchStatus.indicativeOffer,
  FundingMatchStatus.awaitingClientDecision,
  FundingMatchStatus.clientAccepted,
  FundingMatchStatus.inUnderwriting,
  FundingMatchStatus.underwrittenOffer,
  FundingMatchStatus.paidOut,
]);

export function getClosedStatus() {
  return [
    FundingMatchStatus.lenderDecline,
    FundingMatchStatus.lenderRejected,
    FundingMatchStatus.clientDecline,
    FundingMatchStatus.nonResponsive,
  ];
}

export function getFlowStatusesOrdered() {
  return [
    FundingMatchStatus.inReview,
    FundingMatchStatus.contactMade,
    FundingMatchStatus.indicativeOffer,
    FundingMatchStatus.awaitingClientDecision,
    FundingMatchStatus.inUnderwriting,
    FundingMatchStatus.underwrittenOffer,
    FundingMatchStatus.professionsInstructed,
    FundingMatchStatus.professionsSignOff,
    FundingMatchStatus.finalCreditSignOff,
    FundingMatchStatus.paidOut,
    FundingMatchStatus.lenderDecline,
    FundingMatchStatus.clientDecline,
    FundingMatchStatus.nonResponsive,
  ];
}

export function isInProgressState(type) {
  return getInProgressStatus().indexOf(type) >= 0;
}

export const isUnderOffer = (type) => underOfferStatuses.indexOf(type) >= 0;

export function isClosedState(type) {
  return getClosedStatus().indexOf(type) >= 0;
}

export const isMatchStatusCancelled = (status) => status === FundingMatchStatus.cancelled;

export const isMatchStatusDeclined = (status) => {
  const { clientDecline, lenderDecline, nonResponsive } = FundingMatchStatus;

  return [clientDecline, lenderDecline, nonResponsive].includes(status);
};

export const isMatchStatusUnderwrittenOrPaidOut = (status) => {
  const { underwrittenOffer, paidOut } = FundingMatchStatus;

  return [underwrittenOffer, paidOut].includes(status);
};

export const isMatchAtOffer = (status) =>
  [FundingMatchStatus.indicativeOffer, FundingMatchStatus.underwrittenOffer].includes(status);

export const getStatusByLabel = (status) =>
  Number(
    Object.keys(FundingMatchStatusLabel).find((key) => FundingMatchStatusLabel[key] === status),
  );
